import Sparkline from "./Sparkline";
import ColumnSparkline from "./ColumnSparkline";

const formatNumber = (num) => {
  if (num === 0) return '0';
  if (num < 1000) return num.toString();
  if (num < 1000000) {
    const k = num / 1000;
    const fixedPoint = num < 10000 ? 1 : 0;
    return k % 1 === 0 ? `${k}K` : `${k.toFixed(fixedPoint)}K`;
  }
  const m = num / 1000000;
  const fixedPoint = num < 10000000 ? 1 : 0;
  return m % 1 === 0 ? `${m}M` : `${m.toFixed(fixedPoint)}M`;
};

// Client Channel Usage Table Component
export default function TimeSeriesTable({ data, chartOptions = {}, dimensionInfo }) {
  console.log({ data, chartOptions, dimensionInfo })
  const processedData = useMemo(() => {
    if (!data || data.length === 0) return null;

    const getClientName = (d) => {
      if (chartOptions.rowLabel) {
        var label = chartOptions.rowLabel(d, dimensionInfo);
      }
      if (!label || label === '') {
        label = d.clientName || 'Client ' + String(d.clientId)
      }

      return label;
    }
    // Parse timestamps and find min/max
    const parsedData = data.map(d => ({
      ...d,
      timestamp: d.timestamp ? new Date(d.timestamp).getTime() : new Date(d.time).getTime(),
      clientName: getClientName(d),
      kind: d.kind || d.eventsKind
    }));

    console.log({ parsedData })

    // Find unique clients, channels, and time range
    const clients = [...new Set(parsedData.map(d => d.clientName))].sort();
    const channels = ['email', 'text', 'whatsapp', 'call', 'flow', 'donation']
    const timestamps = [...new Set(parsedData.map(d => d.timestamp))].sort();

    console.log({ clients, channels, timestamps })

    // Group data by client and channel
    const grouped = {};
    clients.forEach(client => {
      grouped[client] = {};
      channels.forEach(channel => {
        grouped[client][channel] = {
          timeseries: new Map(),
          total: 0
        };
      });
    });

    // Populate with actual data
    parsedData.forEach(d => {
      if (grouped[d.clientName] && grouped[d.clientName][d.kind]) {
        grouped[d.clientName][d.kind].timeseries.set(d.timestamp, d.count);
        grouped[d.clientName][d.kind].total += d.count;
      }
    });

    // Convert timeseries maps to arrays with 0-filled gaps
    const tableData = clients.map(client => {
      const clientData = { clientName: client };

      channels.forEach(channel => {
        const series = [];
        timestamps.forEach(ts => {
          const value = grouped[client][channel].timeseries.get(ts) || 0;
          series.push(value);
        });

        clientData[channel] = {
          total: grouped[client][channel].total,
          sparklineData: series
        };
      });

      return clientData;
    });

    return {
      clients,
      channels,
      tableData,
      timestamps: timestamps.map(ts => new Date(ts).toLocaleDateString())
    };
  }, [data]);

  if (!processedData) {
    return <div className="empty-cell">No data available</div>;
  }

  // Channel colors
  const channelColors = {
    email: '#3b82f6',
    whatsapp: '#10b981',
    text: '#f59e0b',
    call: '#8b5cf6',
    flow: '#ef4444',
    donation: '#06b6d4'
  };

  return <div className="time-series-table">
    <table className="usage-table">
      <thead>
        <tr>
          <th>Client</th>
          {processedData.channels.map(channel => (
            <th key={channel} className="center">
              <div className="channel-header">
                <span
                  className="channel-dot"
                  style={{ backgroundColor: channelColors[channel] || '#6b7280' }}
                />
                <span>{channel}</span>
              </div>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {processedData.tableData.map((row) => (
          <tr key={row.clientName}>
            <td className="client-name">
              {row.clientName}
            </td>
            {processedData.channels.map(channel => {
              const cellData = row[channel];
              const hasData = cellData.total > 0;
              const maxInColumn = Math.max(...processedData.tableData.map(r => r[channel].total));
              const barWidth = (cellData.total / maxInColumn) * 100;
              const relativeSize = cellData.total / maxInColumn;

              return (
                <td key={channel}>
                  {hasData ? (
                    <div className="data-cell">
                      <div className="size-bar" style={{ width: `${barWidth}%` }} />
                      <span
                        className="data-value"
                        style={{
                          fontWeight: relativeSize > 0.7 ? 700 : relativeSize > 0.3 ? 600 : 500
                        }}>
                        {formatNumber(cellData.total)}
                      </span>
                      <div className="sparkline-wrapper">
                        <ColumnSparkline
                          data={cellData.sparklineData}
                          width={60}
                          height={25}
                          color={channelColors[channel] || '#6b7280'}
                          strokeWidth={1}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="empty-cell">-</div>
                  )}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr>
          <td>Total</td>
          {processedData.channels.map(channel => {
            const channelTotal = processedData.tableData.reduce(
              (sum, row) => sum + row[channel].total,
              0
            );
            const channelSparkline = processedData.timestamps.map((_, idx) =>
              processedData.tableData.reduce((sum, row) => sum + row[channel].sparklineData[idx], 0)
            );

            return (
              <td key={channel}>
                <div className="data-cell">
                  <span className="data-value">
                    {formatNumber(channelTotal)}
                  </span>
                  <div className="sparkline-wrapper">
                    <ColumnSparkline
                      data={channelSparkline}
                      width={60}
                      height={25}
                      color={channelColors[channel] || '#6b7280'}
                      strokeWidth={1.5}
                      fill={true}
                    />
                  </div>
                </div>
              </td>
            );
          })}
        </tr>
      </tfoot>
    </table>
  </div>;
};
